/**
 * Wire-to-domain adaptation for the plan card.
 *
 * The card reads a plan straight off the plans response, so something has to turn the API's stem,
 * alignment mode and patient into the domain values the card formats. That conversion is boundary
 * work and belongs here rather than in the view: a `.vue` cannot import `@/api/*` at all, and
 * neither can a `*ViewModel.ts` — `src/views/**\/api/**` is the folder the rule leaves open for
 * exactly this.
 *
 * Everything here is a pure function of its argument. The card keeps the formatting, which is
 * domain work it may do directly.
 *
 * This is a holding position, not the shape to copy. The card still takes wire types as props and
 * reads snake_case fields off them, so the conversion happens per render rather than once; the fix
 * is to map ApiPlan to a domain Plan in the plans store at load time, after which this module is
 * not needed. Until then, do not read `views/*\/api/**` as the accepted home for wire conversion.
 */

import { type ApiAlignmentMode, alignmentMode } from '@/api/alignmentMode';
import { type CaseApiPatient, formatCaseApiPatient } from '@/api/caseSettings';
import { catalogStem } from '@/api/catalog/catalogStem';
import type { ApiCompleteTemplate } from '@/api/template/completeTemplate';
import type { AlignmentMode } from '@/domain/anatomy/pelvis/alignment';
import type { CatalogStem } from '@/domain/catalog/stem';

/**
 * The stem this plan templates, as a domain stem.
 *
 * `catalogStem` asserts the wire values are ones the domain knows, so an unrecognised system,
 * type, size or offset is refused here rather than rendered as a broken label.
 */
export function planStem(template: ApiCompleteTemplate): CatalogStem {
    return catalogStem(template.stem);
}

/**
 * The coordinate system the cup angles of this case were measured in, or null when the case has
 * no surgical specification yet.
 */
export function planCupAlignment(apiMode: ApiAlignmentMode | undefined): AlignmentMode | null {
    return apiMode ? alignmentMode(apiMode) : null;
}

/** The patient's name as the card shows it, empty when there is no name to show. */
export function planPatientName(patient: CaseApiPatient | null | undefined): string {
    return patient ? formatCaseApiPatient(patient) : '';
}
